/**
 * #4889: diligence field VALUES are read/written through the generic DB-driven
 * `fields.values: [FieldValue!]` array + updateXFieldValues mutations (the typed
 * per-field surface was deleted in Phase 6). FieldValue is a typed envelope
 * (fieldKey/state/asOfDate/notApplicable/documentIds/documents/citations) with
 * only `value` as JSON. Consumers look a field up by fieldKey in
 * `data[owner].fields.values` instead of `data[owner].fields[key]`.
 */

/**
 * The typed FieldValue selection set (value is JSON; the rest is typed).
 *
 * `id`, `ownerType` and the audit fields are NOT optional extras — the publish
 * modal depends on all of them:
 *  - `ownerType` builds the "${ownerType}.${fieldKey}" cache key
 *    (useReadFieldFromCache). Without it the lookup misses, the selector returns
 *    nullMetadata (displayName: "none") and the row renders "updated none" with
 *    every value diffed against an absent published field ("+ null").
 *  - `id` is the React key for the draft rows.
 *  - `modifyUser` drives which drafts are pre-selected for publishing
 *    (`draftFields.filter(f => f.modifyUser === email)`); undefined selects
 *    nothing, so Publish silently saved zero fields.
 * Mirrors the investmentReport worker's selection, which already requests these.
 */
export const FIELD_VALUE_SELECTION = `values {
  id
  ownerType
  fieldKey
  value
  state
  asOfDate
  notApplicable
  documentIds
  createDate
  createUser
  modifyDate
  modifyUser
  documents {
    id
    document {
      id
      name
      date
      modifyUser
      signedUrl
      signedThumbnailUrl
    }
  }
  citations { type id }
}`;

/** Read one field's value envelope from a raw query result (find by fieldKey). */
export function getFieldValueFromData(
  data: any,
  ownerType: string,
  fieldKey: string
): any {
  const fields = data?.[ownerType]?.fields;
  if (!fields) return undefined;
  return (fields.values ?? []).find(
    (v: { fieldKey: string }) => v.fieldKey === fieldKey
  );
}

/** Convert a `fields.values` array into a fieldKey->value map. */
export function fieldValuesToMap(ownerFields: any): Record<string, any> {
  if (!ownerFields) return {};
  return Object.fromEntries(
    (ownerFields.values ?? []).map((v: { fieldKey: string }) => [v.fieldKey, v])
  );
}

/**
 * Convert the store's per-fieldKey FieldInput map into the generic
 * [FieldValueInput] array the updateXFieldValues / deleteXFieldValues mutations
 * take (#4889). Returns null when there's nothing to write so an empty selection
 * isn't emitted.
 */
export function toFieldValueInputs(
  fields: { [key: string]: Record<string, unknown> } | undefined
): Record<string, unknown>[] | null {
  if (!fields || Object.keys(fields).length === 0) return null;
  return Object.entries(fields).map(([fieldKey, f]) => ({
    fieldKey,
    value: f.value,
    asOfDate: f.asOfDate ?? null,
    periodType: f.periodType ?? null,
    notApplicable: f.notApplicable ?? null,
    documentIds: f.documentIds ?? null,
  }));
}

/** The generic write mutation field for one owner, e.g. updateInvestmentFieldValues(...). */
export function genericFieldsMutationField(
  operation: "updateFields" | "deleteFields",
  ownerType: "investment" | "firm" | "company",
  idArg: string,
  valuesVar: string
): string {
  const verb = operation === "updateFields" ? "update" : "delete";
  const Owner = ownerType.charAt(0).toUpperCase() + ownerType.slice(1);
  return `${verb}${Owner}FieldValues(
    ${idArg}: $${idArg}, state: $state, values: $${valuesVar}
  ) {
    ${idArg}
    state
    ${FIELD_VALUE_SELECTION}
  }`;
}
